import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import { SubscriptionBanner, DemoBanner, FrozenScreen, FeatureGate } from "@/components/SubscriptionGuards";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PortfoliosPage from "@/pages/PortfoliosPage";
import MapPage from "@/pages/MapPage";
import ShootsPage from "@/pages/ShootsPage";
import DronePage from "@/pages/DronePage";
import AdminDronePage from "@/pages/AdminDronePage";
import ScansPage from "@/pages/ScansPage";
import ContractsPage from "@/pages/ContractsPage";
import DeedsPage from "@/pages/DeedsPage";
import OfficeSettingsPage from "@/pages/OfficeSettingsPage";
import CalendarPage from "@/pages/CalendarPage";
import TodosPage from "@/pages/TodosPage";
import ReportsPage from "@/pages/ReportsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import { GlobalPopups } from "@/components/planner/GlobalPopups";
import { TopBanner } from "@/components/TopBanner";
import { ChatWidget } from "@/components/support/ChatWidget";
import SupportPage from "@/components/support/TicketCenter";
import AdminSupportPage from "@/pages/AdminSupportPage";
import PublicHome from "@/pages/PublicHome";
import CmsPage from "@/pages/CmsPage";

const CmsPreview = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role !== "SUPER_ADMIN") return <Navigate to="/login" replace />;
  return <PublicHome preview />;
};
import FinancePage from "@/pages/FinancePage";
import VehiclesPage from "@/pages/VehiclesPage";
import CustomersPage from "@/pages/CustomersCrmPage";
import UsersPage from "@/pages/UsersPage";
import OfficesPage from "@/pages/OfficesPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AdminSubscriptionsPage from "@/pages/AdminSubscriptionsPage";
import PackagesPage from "@/pages/PackagesPage";
import CampaignsPage from "@/pages/CampaignsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import StaffPage from "@/pages/StaffPage";
import LogsPage from "@/pages/LogsPage";
import PlatformFinancePage from "@/pages/PlatformFinancePage";
import DataAccessAdminPage from "@/pages/DataAccessAdminPage";
import OfficeDataAccessPage from "@/pages/OfficeDataAccessPage";
import MonitoringPage from "@/pages/MonitoringPage";
import PoolPage from "@/pages/PoolPage";
import AdminPoolPage from "@/pages/AdminPoolPage";
import AdminBackupsPage from "@/pages/AdminBackupsPage";
import OfficeBackupsPage from "@/pages/OfficeBackupsPage";
import FinanceCenterPage from "@/pages/FinanceCenterPage";
import AdminDronePlusPage from "@/pages/AdminDronePlusPage";
import OpsDronePage from "@/pages/OpsDronePage";
import ParcelPage from "@/pages/ParcelPage";
import { LegalGate } from "@/components/legal/LegalGate";
import MyAgreementsPage from "@/pages/MyAgreementsPage";
import AdminLegalPage from "@/pages/AdminLegalPage";
import AdminGlossaryPage from "@/pages/AdminGlossaryPage";
import PublicGlossaryPage, { PanelGlossaryPage } from "@/pages/PublicGlossaryPage";
import "@/App.css";

const PLATFORM = ["SUPER_ADMIN", "REPRESENTATIVE", "PLATFORM_ACCOUNTANT"];

const Protected = ({ roles, perm, children }) => {
  const { user, hasPerm } = useAuth();
  if ((roles && !roles.includes(user.role)) || (perm && !hasPerm(perm))) {
    return (
      <div className="p-10">
        <div className="max-w-md mx-auto bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold text-amber-900 mb-2">Yetkisiz Erişim</h2>
          <p className="text-amber-700 text-sm">Bu sayfayı görüntüleme yetkiniz yok.</p>
        </div>
      </div>
    );
  }
  return children;
};

const Layout = ({ children }) => {
  const { user, loading, subscription, legalPending } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (legalPending > 0) return <LegalGate />;
  const frozen = subscription?.status === "FROZEN" && location.pathname !== "/subscription";
  return (
    <AppShell>
      {user.office_id && !frozen && <TopBanner />}
      <DemoBanner />
      <SubscriptionBanner />
      <GlobalPopups />
      {frozen ? <FrozenScreen /> : children}
      {user.office_id && <ChatWidget />}
    </AppShell>
  );
};

const page = (el, { roles, feature, perm } = {}) => (
  <Layout>
    <Protected roles={roles} perm={perm}>{feature ? <FeatureGate feature={feature}>{el}</FeatureGate> : el}</Protected>
  </Layout>
);

const Home = () => {
  const { user } = useAuth();
  return user && PLATFORM.includes(user.role) ? <AdminDashboard /> : <DashboardPage />;
};

const OFFICE = ["BROKER", "ACCOUNTANT", "CONSULTANT"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<PublicHome />} />
          <Route path="/cms-preview" element={<CmsPreview />} />
          <Route path="/sozluk" element={<PublicGlossaryPage />} />
          <Route path="/sozluk/:slug" element={<PublicGlossaryPage />} />
          <Route path="/parcels" element={page(<ParcelPage />)} />
          <Route path="/agreements" element={page(<MyAgreementsPage />, { roles: OFFICE })} />
          <Route path="/admin/legal" element={page(<AdminLegalPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/dictionary" element={page(<PanelGlossaryPage />)} />
          <Route path="/dictionary/:slug" element={page(<PanelGlossaryPage />)} />
          <Route path="/admin/glossary" element={page(<AdminGlossaryPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/admin/cms" element={page(<CmsPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/panel" element={page(<Home />)} />
          <Route path="/portfolios" element={page(<PortfoliosPage />, { roles: OFFICE, feature: "PORTFOLIO" })} />
          <Route path="/shoots" element={page(<ShootsPage />, { roles: OFFICE, feature: "SHOOTS" })} />
          <Route path="/drone" element={page(<DronePage />, { roles: ["BROKER", "CONSULTANT"], feature: "DRONE" })} />
          <Route path="/scans" element={page(<ScansPage />, { roles: OFFICE, feature: "SCAN" })} />
          <Route path="/contracts" element={page(<ContractsPage />, { roles: OFFICE })} />
          <Route path="/deeds" element={page(<DeedsPage />, { roles: OFFICE, feature: "DEED" })} />
          <Route path="/settings" element={page(<OfficeSettingsPage />, { roles: ["BROKER"] })} />
          <Route path="/calendar" element={page(<CalendarPage />, { roles: OFFICE })} />
          <Route path="/todos" element={page(<TodosPage />, { roles: OFFICE })} />
          <Route path="/reports" element={page(<ReportsPage />, { roles: OFFICE, feature: "PERFORMANCE" })} />
          <Route path="/admin/settings" element={page(<AdminSettingsPage />, { roles: PLATFORM })} />
          <Route path="/support" element={page(<SupportPage />, { roles: OFFICE })} />
          <Route path="/admin/support" element={page(<AdminSupportPage />, { roles: PLATFORM, perm: "SUPPORT_MANAGE" })} />
          <Route path="/admin/drone" element={page(<AdminDronePage />, { roles: PLATFORM, perm: "DRONE_MANAGE" })} />
          <Route path="/map" element={page(<MapPage />, { roles: OFFICE, feature: "MAP" })} />
          <Route path="/finance" element={page(<FinancePage />, { roles: ["BROKER", "ACCOUNTANT"], feature: "FINANCE" })} />
          <Route path="/vehicles" element={page(<VehiclesPage />, { roles: OFFICE, feature: "VEHICLES" })} />
          <Route path="/customers" element={page(<CustomersPage />, { roles: OFFICE, feature: "CRM" })} />
          <Route path="/users" element={page(<UsersPage />, { roles: ["BROKER", "SUPER_ADMIN"] })} />
          <Route path="/subscription" element={page(<SubscriptionPage />, { roles: ["BROKER", "ACCOUNTANT"] })} />
          <Route path="/data-access" element={page(<OfficeDataAccessPage />, { roles: ["BROKER"] })} />
          <Route path="/offices" element={page(<OfficesPage />, { roles: PLATFORM, perm: "OFFICE_VIEW" })} />
          <Route path="/admin/subscriptions" element={page(<AdminSubscriptionsPage />, { roles: PLATFORM, perm: "SUBSCRIPTION_VIEW" })} />
          <Route path="/admin/packages" element={page(<PackagesPage />, { roles: PLATFORM, perm: "PACKAGES_MANAGE" })} />
          <Route path="/admin/campaigns" element={page(<CampaignsPage />, { roles: PLATFORM, perm: "CAMPAIGNS_MANAGE" })} />
          <Route path="/admin/finance" element={page(<PlatformFinancePage />, { roles: PLATFORM, perm: "FINANCE_REPORTS" })} />
          <Route path="/admin/logs" element={page(<LogsPage />, { roles: PLATFORM, perm: "LOGS_VIEW" })} />
          <Route path="/admin/staff" element={page(<StaffPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/admin/monitoring" element={page(<MonitoringPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/pool" element={page(<PoolPage />, { roles: OFFICE })} />
          <Route path="/admin/pool" element={page(<AdminPoolPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/admin/backups" element={page(<AdminBackupsPage />, { roles: ["SUPER_ADMIN"] })} />
          <Route path="/backups" element={page(<OfficeBackupsPage />, { roles: ["BROKER"] })} />
          <Route path="/admin/fin" element={page(<FinanceCenterPage />, { roles: PLATFORM, perm: "FIN_VIEW" })} />
          <Route path="/admin/drone-plus" element={page(<AdminDronePlusPage />, { roles: PLATFORM, perm: "DRONE_MANAGE" })} />
          <Route path="/ops/drone" element={page(<OpsDronePage />, { roles: ["SUPER_ADMIN", "REPRESENTATIVE"], perm: "DRONE_OPS" })} />
          <Route path="/admin/data-access" element={page(<DataAccessAdminPage />, { roles: ["SUPER_ADMIN"] })} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
